// src/components/module/usuarios/FormularioUsuario.tsx
// Rediseño corporativo estilo Notion/Linear
import React, { useState, useEffect, useContext } from "react";
import { toast } from '../../../utils/toast';
import {
  Dialog,
  DialogContent,
  Button,
  TextField,
  FormControl,
  Select,
  MenuItem,
  FormHelperText,
  Grid,
  Box,
  Typography,
  IconButton,
  Stack,
  InputAdornment,
  alpha,
  CircularProgress,
} from "@mui/material";
import { Close as CloseIcon, Save, Visibility, VisibilityOff, PersonAdd } from "@mui/icons-material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { CreateUsuarioDto, Usuario } from "../../../components/types/usuario.types";
import { useRolesApi } from "../../../hooks/RolesApi";
import { usePerfilesApi } from "../../../hooks/PerfilesApi";
import { useEmpleadosApi } from "../../../services/api/empleadosApi";
import { DarkModeContext } from "../../../context/darkModeContext";
const P = {
  light: { bg: "#FFFFFF", bgS: "#F7F7F5", bgT: "#F1F1EF", text: "#37352F", textS: "#6B6B6B", textT: "#9B9A97", border: "#E9E9E7", borderH: "#D3D3D0", accent: "#2383E2", accentBg: "#E8F0FE", error: "#E03E3E", hover: "rgba(55,53,47,0.04)" },
  dark: { bg: "#191919", bgS: "#202020", bgT: "#2B2B2B", text: "#E8E8E6", textS: "#9B9A97", textT: "#6B6B6B", border: "#333333", borderH: "#444444", accent: "#529CCA", accentBg: "#143A52", error: "#D44C47", hover: "rgba(255,255,255,0.04)" },
};

const FieldLabel: React.FC<{ label: string; required?: boolean; color: string }> = ({ label, required, color }) => (
  <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color, mb: 0.5, textTransform: "uppercase", letterSpacing: "0.04em" }}>
    {label}{required && <Box component="span" sx={{ color: "#E03E3E", ml: 0.3 }}>*</Box>}
  </Typography>
);

interface FormularioUsuarioProps { open: boolean; onClose: () => void; onSubmit: (values: CreateUsuarioDto) => Promise<void>; initialValues?: Partial<Usuario>; isEdit?: boolean; }
interface FormValues { IdEmpleado: number | ""; IdPerfil: number | ""; IdRol: number | ""; Nombre: string; Usuario: string; Contrasena: string; Activo: boolean; isEdit: boolean; }
export interface PerfilItem { Id: number; Nombre: string; Activo: boolean; Eliminado: boolean; }
export interface RolItem { Id: number; Nombre: string; Activo: boolean; Eliminado: boolean; }
export interface EmpleadoItem { Id: number; Nombre: string; Apellidos: string; NombreCompleto: string; }

const validationSchema = Yup.object().shape({
  Usuario: Yup.string().required("Requerido").min(8, "Mínimo 8 caracteres").matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).+$/, "Debe tener mayúscula, minúscula, número y especial"),
  Contrasena: Yup.string().when("isEdit", { is: false, then: () => Yup.string().required("Requerida").min(10, "Mínimo 10 caracteres").matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).+$/, "Debe tener mayúscula, minúscula, número y especial") }),
  IdPerfil: Yup.mixed().required("Requerido").transform((v) => (v === "" ? undefined : Number(v))),
  IdRol: Yup.mixed().required("Requerido").transform((v) => (v === "" ? undefined : Number(v))),
  IdEmpleado: Yup.mixed().transform((v) => (v === "" ? undefined : Number(v))),
  Nombre: Yup.string(), Activo: Yup.boolean(),
});

export const FormularioUsuario: React.FC<FormularioUsuarioProps> = ({ open, onClose, onSubmit, initialValues, isEdit = false }) => {
  const [perfiles, setPerfiles] = useState<PerfilItem[]>([]);
  const [roles, setRoles] = useState<RolItem[]>([]);
  const [empleados, setEmpleados] = useState<EmpleadoItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const { darkMode } = useContext(DarkModeContext);
  const p = darkMode ? P.dark : P.light;
  const { getAll: getPerfiles } = usePerfilesApi();
  const { getAll: getRoles } = useRolesApi();
  const { getAll: getEmpleados } = useEmpleadosApi();

  useEffect(() => {
    if (!open) return;
    const load = async () => {
      setLoading(true);
      try {
        const [pf, rl, em] = await Promise.all([getPerfiles(), getRoles(), getEmpleados()]);
        setPerfiles((pf as PerfilItem[]).filter((x) => !x.Eliminado));
        setRoles((rl as RolItem[]).filter((x) => !x.Eliminado));
        setEmpleados(em.map((e: any) => ({ ...e, NombreCompleto: `${e.Nombre} ${e.Apellidos}`.trim() })));
      } catch { toast.error("Error cargando datos"); }
      finally { setLoading(false); }
    };
    load();
  }, [open]);

  const formik = useFormik<FormValues>({
    initialValues: { IdEmpleado: initialValues?.IdEmpleado || "", IdPerfil: initialValues?.IdPerfil || "", IdRol: initialValues?.IdRol || "", Nombre: initialValues?.Nombre || "", Usuario: initialValues?.Usuario || "", Contrasena: "", Activo: initialValues?.Activo ?? true, isEdit },
    validationSchema, enableReinitialize: true,
    onSubmit: async (values) => {
      setLoading(true);
      try { await onSubmit({ IdEmpleado: values.IdEmpleado ? Number(values.IdEmpleado) : undefined, IdPerfil: Number(values.IdPerfil), IdRol: Number(values.IdRol), Nombre: values.Nombre || undefined, Usuario: values.Usuario, Contrasena: values.Contrasena, Activo: values.Activo }); formik.resetForm(); onClose(); }
      catch { } finally { setLoading(false); }
    },
  });

  const handleClose = () => { formik.resetForm(); onClose(); };
  const inputSx = { "& .MuiInputBase-root": { bgcolor: p.bgS } };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: "14px", border: `1px solid ${p.border}`, bgcolor: p.bg, overflow: "hidden" } }}>
        <Box sx={{ px: 3, py: 2.5, display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: `1px solid ${p.border}` }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box sx={{ width: 40, height: 40, borderRadius: "10px", bgcolor: p.accentBg, display: "flex", alignItems: "center", justifyContent: "center", color: p.accent }}><PersonAdd sx={{ fontSize: 20 }} /></Box>
            <Box>
              <Typography sx={{ fontSize: "1.05rem", fontWeight: 700, color: p.text }}>{isEdit ? "Editar Usuario" : "Crear Usuario"}</Typography>
              <Typography sx={{ fontSize: "0.75rem", color: p.textT }}>Complete la información del usuario</Typography>
            </Box>
          </Stack>
          <IconButton onClick={handleClose} sx={{ width: 32, height: 32, borderRadius: "8px", color: p.textT, "&:hover": { bgcolor: p.bgT } }}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
        </Box>

        <form onSubmit={formik.handleSubmit}>
          <DialogContent sx={{ p: 3, bgcolor: p.bgS }}>
            <Grid container spacing={2.5}>
              <Grid item xs={12} md={6}>
                <FieldLabel label="Empleado" color={p.textT} />
                <FormControl fullWidth size="small" error={formik.touched.IdEmpleado && Boolean(formik.errors.IdEmpleado)}>
                  <Select name="IdEmpleado" value={formik.values.IdEmpleado} onChange={formik.handleChange} onBlur={formik.handleBlur} displayEmpty sx={{ ...inputSx["& .MuiInputBase-root"], bgcolor: p.bgS }}>
                    <MenuItem value=""><em>Ninguno (opcional)</em></MenuItem>
                    {empleados.map((e) => <MenuItem key={e.Id} value={e.Id}>{e.NombreCompleto}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FieldLabel label="Nombre" color={p.textT} />
                <TextField size="small" fullWidth name="Nombre" value={formik.values.Nombre} onChange={formik.handleChange} onBlur={formik.handleBlur} sx={inputSx} />
              </Grid>
              <Grid item xs={12} md={6}>
                <FieldLabel label="Perfil" required color={p.textT} />
                <FormControl fullWidth size="small" error={formik.touched.IdPerfil && Boolean(formik.errors.IdPerfil)}>
                  <Select name="IdPerfil" value={formik.values.IdPerfil} onChange={formik.handleChange} onBlur={formik.handleBlur} sx={{ bgcolor: p.bgS }}>
                    {perfiles.map((pf) => <MenuItem key={pf.Id} value={pf.Id}>{pf.Nombre}</MenuItem>)}
                  </Select>
                  {formik.touched.IdPerfil && formik.errors.IdPerfil && <FormHelperText>{formik.errors.IdPerfil}</FormHelperText>}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FieldLabel label="Rol" required color={p.textT} />
                <FormControl fullWidth size="small" error={formik.touched.IdRol && Boolean(formik.errors.IdRol)}>
                  <Select name="IdRol" value={formik.values.IdRol} onChange={formik.handleChange} onBlur={formik.handleBlur} sx={{ bgcolor: p.bgS }}>
                    {roles.map((r) => <MenuItem key={r.Id} value={r.Id}>{r.Nombre}</MenuItem>)}
                  </Select>
                  {formik.touched.IdRol && formik.errors.IdRol && <FormHelperText>{formik.errors.IdRol}</FormHelperText>}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FieldLabel label="Usuario" required color={p.textT} />
                <TextField size="small" fullWidth name="Usuario" value={formik.values.Usuario} onChange={formik.handleChange} onBlur={formik.handleBlur}
                  error={formik.touched.Usuario && Boolean(formik.errors.Usuario)} helperText={formik.touched.Usuario && formik.errors.Usuario} sx={inputSx} />
              </Grid>
              <Grid item xs={12} md={6}>
                <FieldLabel label={isEdit ? "Nueva Contraseña" : "Contraseña"} required={!isEdit} color={p.textT} />
                <TextField size="small" fullWidth name="Contrasena" type={showPwd ? "text" : "password"} value={formik.values.Contrasena} onChange={formik.handleChange} onBlur={formik.handleBlur}
                  error={formik.touched.Contrasena && Boolean(formik.errors.Contrasena)} helperText={formik.touched.Contrasena && formik.errors.Contrasena}
                  InputProps={{ endAdornment: <InputAdornment position="end"><IconButton onClick={() => setShowPwd(!showPwd)} edge="end" size="small">{showPwd ? <VisibilityOff sx={{ fontSize: 18 }} /> : <Visibility sx={{ fontSize: 18 }} />}</IconButton></InputAdornment> }}
                  sx={inputSx} />
              </Grid>
            </Grid>

            {(!isEdit || formik.values.Contrasena) && (
              <Box sx={{ mt: 2.5, p: 2, borderRadius: "8px", bgcolor: p.accentBg, border: `1px solid ${alpha(p.accent, 0.2)}` }}>
                <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: p.textT, textTransform: "uppercase", mb: 0.5 }}>Requisitos de contraseña</Typography>
                <Typography sx={{ fontSize: "0.75rem", color: p.textS, lineHeight: 1.8 }}>
                  Mínimo 10 caracteres · Al menos una mayúscula · Una minúscula · Un número · Un carácter especial
                </Typography>
              </Box>
            )}
          </DialogContent>

          <Box sx={{ px: 3, py: 2, borderTop: `1px solid ${p.border}`, bgcolor: p.bg, display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
            <Button onClick={handleClose} sx={{ color: p.textS, fontSize: "0.82rem", fontWeight: 600, borderRadius: "8px", "&:hover": { bgcolor: p.hover } }}>Cancelar</Button>
            <Button type="submit" variant="contained" disableElevation disabled={loading || !formik.isValid || formik.isSubmitting}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Save sx={{ fontSize: 16 }} />}
              sx={{ bgcolor: p.accent, color: "#FFF", px: 3, fontSize: "0.82rem", fontWeight: 600, borderRadius: "8px", "&:hover": { bgcolor: darkMode ? "#6BAFDB" : "#1B6FC2" } }}>
              {formik.isSubmitting ? "Guardando..." : "Guardar"}
            </Button>
          </Box>
        </form>
      </Dialog>
  );
};
